/**
 * Ambient backyard frog — shared behavior for yards that opt in.
 *
 * Two independent frogs per yard. Outside Friends / playable characters:
 * no dialogue, quests, inventory, or combat. Pure cause-and-effect hopping.
 */

import type { SpawnPoint, WalkObstacle } from '@/world-prototype/types';
import { creatureDistance } from '@/world-prototype/creatures/creatureGeometry';
import {
  isNearPoint,
  WILDLIFE_SLEEP_TIMING,
} from '@/world-prototype/creatures/wildlifeSleep';
import {
  pointInAnyObstacle,
  pointInPolygon,
} from '@/world-prototype/movement/walkArea';
import {
  pointInFrogExclusionZone,
  type FrogExclusionBounds,
} from '@/world-prototype/creatures/frogExclusionZones';

/** Art poses from Frog-Master (sit) and Frog-2-master (hop). */
export type FrogPose = 'sit' | 'hop' | 'sleep';

export type FrogPhase =
  | 'idle'
  | 'crouch'
  | 'hopping'
  | 'goToSleep'
  | 'sleeping'
  | 'waking'
  | 'headCrouch'
  | 'headJumpTo'
  | 'perched'
  | 'headJumpDown'
  | 'headCooldown';

export type FrogFacing = 'left' | 'right';

/** One frog's home patch inside a yard. */
export interface FrogInstanceConfig {
  id: string;
  home: SpawnPoint;
  /** Max hop distance from home, in world-width units. */
  roamRadius: number;
  /**
   * 0–1 multiplier that staggers idle timers so paired frogs do not hop in sync.
   */
  idlePhase: number;
}

/** Per-yard geography shared by both frogs. */
export interface FrogYardConfig {
  yardId: string;
  /** Legal grass for frogs in this yard (world coords 0–1). */
  roamPolygon: readonly SpawnPoint[];
  frogs: readonly FrogInstanceConfig[];
}

export const FROG_TIMING = {
  idleMs: [3_000, 8_000],
  crouchMs: [180, 320],
  hopMs: [380, 520],
  fleeHopMs: [320, 450],
  /** Brief squat before a scared hop. */
  reactCrouchMs: [140, 260],
} as const;

export const FROG_HOP_DISTANCE = { min: 0.018, max: 0.048 } as const;
export const FROG_FLEE_DISTANCE = { min: 0.035, max: 0.075 } as const;

/** Player close enough to make a frog hop away. */
export const FROG_COMFORT_RADIUS = 0.075;

/** Frogs refuse to land on the player's feet. */
export const FROG_PLAYER_CLEARANCE = 0.038;

/** Peak hop lift as a fraction of rendered height. */
export const FROG_HOP_LIFT = 0.3;

/** Higher arc for the playful head-jump. */
export const FROG_HEAD_JUMP_LIFT = 0.52;

export const FROG_HEAD_TIMING = {
  headCrouchMs: [220, 320] as const,
  headJumpToMs: [480, 620] as const,
  perchMs: [1_400, 2_000] as const,
  headJumpDownMs: [420, 560] as const,
  headCooldownMs: [2_000, 4_000] as const,
  perchSettleMs: 220,
} as const;

export const FROG_HIT_MIN_CSS_PX = 64;

interface FrogHop {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  elapsedMs: number;
  durationMs: number;
}

export interface FrogState {
  id: string;
  phase: FrogPhase;
  pose: FrogPose;
  x: number;
  y: number;
  facing: FrogFacing;
  timerMs: number;
  hop: FrogHop | null;
  liftT: number;
  home: SpawnPoint;
  roamRadius: number;
  idlePhase: number;
  /** True when the next hop should flee from the player. */
  fleeQueued: boolean;
  /** Head-jump target while jumping to / from the character. */
  headTarget: SpawnPoint | null;
  /** 0–1 one-shot settle bounce while perched. */
  perchSettleT: number;
  /** Night tuck-in target while hopping to the den. */
  sleepTarget: SpawnPoint | null;
}

export interface FrogStepContext {
  config: FrogYardConfig;
  player: SpawnPoint | null;
  playerFacing?: 'left' | 'right' | null;
  worldAspect: number;
  obstacles?: readonly WalkObstacle[];
  /** Garden beds + padding — frogs never land here. */
  exclusionZones?: readonly FrogExclusionBounds[];
  /** Live head perch point for the active character. */
  headAnchor?: () => SpawnPoint | null;
  /** Only one frog may perch on the character at a time. */
  tryAcquireHeadPerch?: (frogId: string) => boolean;
  releaseHeadPerch?: (frogId: string) => void;
  rng?: () => number;
}

export function isFrogHeadSequencePhase(phase: FrogPhase): boolean {
  return (
    phase === 'headCrouch' ||
    phase === 'headJumpTo' ||
    phase === 'perched' ||
    phase === 'headJumpDown'
  );
}

export function frogDistance(
  a: SpawnPoint,
  b: SpawnPoint,
  worldAspect: number,
): number {
  return creatureDistance(a, b, worldAspect);
}

export function createFrogState(
  instance: FrogInstanceConfig,
  rng: () => number = Math.random,
): FrogState {
  const idleSpan = FROG_TIMING.idleMs[1] - FROG_TIMING.idleMs[0];
  return {
    id: instance.id,
    phase: 'idle',
    pose: 'sit',
    x: instance.home.x,
    y: instance.home.y,
    facing: rng() < 0.5 ? 'left' : 'right',
    timerMs:
      FROG_TIMING.idleMs[0] +
      instance.idlePhase * idleSpan +
      rng() * idleSpan * 0.35,
    hop: null,
    liftT: 0,
    home: instance.home,
    roamRadius: instance.roamRadius,
    idlePhase: instance.idlePhase,
    fleeQueued: false,
    headTarget: null,
    perchSettleT: 0,
    sleepTarget: null,
  };
}

export function isFrogSleepPhase(phase: FrogPhase): boolean {
  return phase === 'goToSleep' || phase === 'sleeping' || phase === 'waking';
}

/** Night falls — head for the shared frog den. */
export function requestFrogSleep(
  state: FrogState,
  target: SpawnPoint,
  worldAspect: number,
  ctx?: FrogStepContext,
  rng: () => number = Math.random,
): void {
  if (
    ctx &&
    (isFrogHeadSequencePhase(state.phase) || state.phase === 'headCooldown')
  ) {
    cancelFrogHeadSequence(state, ctx, rng);
  }
  state.fleeQueued = false;
  state.headTarget = null;
  state.hop = null;
  state.liftT = 0;
  state.sleepTarget = target;

  if (isNearPoint(state, target, worldAspect)) {
    state.x = target.x;
    state.y = target.y;
    enterFrogSleep(state);
    return;
  }

  state.phase = 'goToSleep';
  state.pose = 'hop';
  beginHop(state, target, pickRange(FROG_TIMING.hopMs, rng));
}

/** Dawn — brief wake beat, then resume ambient hopping. */
export function requestFrogWake(state: FrogState): void {
  if (!isFrogSleepPhase(state.phase)) return;
  state.phase = 'waking';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.sleepTarget = null;
  state.fleeQueued = false;
  state.headTarget = null;
  state.timerMs = WILDLIFE_SLEEP_TIMING.frogWakePauseMs;
}

function enterFrogSleep(state: FrogState): void {
  state.phase = 'sleeping';
  state.pose = 'sleep';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  state.timerMs = 0;
}

function stepFrogSleep(
  state: FrogState,
  dt: number,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  if (state.phase === 'sleeping') return;

  if (state.phase === 'waking') {
    state.timerMs -= dt;
    if (state.timerMs <= 0) {
      settleIdle(state, ctx, rng);
    }
    return;
  }

  if (state.hop) {
    advanceHop(state, dt, FROG_HOP_LIFT);
    if (state.hop) return;
    if (
      state.sleepTarget &&
      isNearPoint(state, state.sleepTarget, ctx.worldAspect)
    ) {
      enterFrogSleep(state);
      return;
    }
  }

  if (state.sleepTarget) {
    state.phase = 'goToSleep';
    state.pose = 'hop';
    beginHop(state, state.sleepTarget, pickRange(FROG_TIMING.hopMs, rng));
  }
}

/**
 * Tap / click — signature head-jump toward the active character.
 * Returns true when the sequence starts.
 */
export function beginHeadJump(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number = Math.random,
): boolean {
  if (isFrogSleepPhase(state.phase)) return false;
  if (state.phase !== 'idle') return false;
  if (!ctx.player || !ctx.headAnchor || !ctx.tryAcquireHeadPerch) return false;
  if (!ctx.tryAcquireHeadPerch(state.id)) return false;

  const target = ctx.headAnchor();
  if (!target) {
    ctx.releaseHeadPerch?.(state.id);
    return false;
  }

  state.phase = 'headCrouch';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  state.headTarget = target;
  state.perchSettleT = 0;
  state.timerMs = pickRange(FROG_HEAD_TIMING.headCrouchMs, rng);
  return true;
}

/** @deprecated Use beginHeadJump for taps; kept for tests that expect flee-on-poke. */
export function pokeFrog(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number = Math.random,
): void {
  if (beginHeadJump(state, ctx, rng)) return;
}

export function cancelFrogHeadSequence(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number = Math.random,
): void {
  if (!isFrogHeadSequencePhase(state.phase)) return;
  ctx.releaseHeadPerch?.(state.id);
  state.headTarget = null;
  state.perchSettleT = 0;
  emergencyLandFrog(state, ctx, rng);
}

export function stepFrog(
  state: FrogState,
  dtMs: number,
  ctx: FrogStepContext,
): FrogState {
  const rng = ctx.rng ?? Math.random;
  const dt = Math.max(0, Math.min(dtMs, 120));

  if (isFrogSleepPhase(state.phase)) {
    stepFrogSleep(state, dt, ctx, rng);
    return state;
  }

  ejectFromExclusionIfNeeded(state, ctx, rng);

  if (isFrogHeadSequencePhase(state.phase) || state.phase === 'headCooldown') {
    stepHeadSequence(state, dt, ctx, rng);
    return state;
  }

  if (
    state.phase === 'idle' &&
    ctx.player &&
    frogDistance(state, ctx.player, ctx.worldAspect) < FROG_COMFORT_RADIUS
  ) {
    queueFlee(state, rng);
    beginReactCrouch(state, rng);
    return state;
  }

  if (state.hop) {
    advanceHop(state, dt, FROG_HOP_LIFT);
    if (state.hop) return state;
    onAmbientHopLand(state, ctx, rng);
    return state;
  }

  state.timerMs -= dt;
  if (state.timerMs > 0) return state;

  switch (state.phase) {
    case 'crouch':
      if (state.fleeQueued) startFleeHop(state, ctx, rng);
      else startRoamHop(state, ctx, rng);
      return state;
    case 'idle':
      beginAmbientCrouch(state, rng);
      return state;
    default:
      return state;
  }
}

function stepHeadSequence(
  state: FrogState,
  dt: number,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  if (state.phase === 'perched') {
    followHeadPerch(state, ctx);
    if (state.perchSettleT > 0) {
      state.perchSettleT = Math.max(
        0,
        state.perchSettleT - dt / FROG_HEAD_TIMING.perchSettleMs,
      );
      state.liftT = state.perchSettleT * 0.14;
    } else {
      state.liftT = 0;
    }
    state.timerMs -= dt;
    if (state.timerMs <= 0) startHeadJumpDown(state, ctx, rng);
    return;
  }

  if (state.phase === 'headCooldown') {
    state.timerMs -= dt;
    if (state.timerMs <= 0) {
      state.phase = 'idle';
      state.pose = 'sit';
      const idleSpan = FROG_TIMING.idleMs[1] - FROG_TIMING.idleMs[0];
      state.timerMs =
        FROG_TIMING.idleMs[0] +
        state.idlePhase * idleSpan * 0.5 +
        rng() * idleSpan;
    }
    return;
  }

  if (state.hop) {
    const lift =
      state.phase === 'headJumpTo' || state.phase === 'headJumpDown'
        ? FROG_HEAD_JUMP_LIFT
        : FROG_HOP_LIFT;
    advanceHop(state, dt, lift);
    if (state.hop) return;
    onHeadHopLand(state, ctx, rng);
    return;
  }

  state.timerMs -= dt;
  if (state.timerMs > 0) return;

  switch (state.phase) {
    case 'headCrouch':
      startHeadJumpTo(state, ctx, rng);
      return;
    default:
      return;
  }
}

function followHeadPerch(state: FrogState, ctx: FrogStepContext): void {
  const anchor = ctx.headAnchor?.();
  if (anchor) {
    state.x = anchor.x;
    state.y = anchor.y;
    state.headTarget = anchor;
  }
  if (ctx.playerFacing) {
    state.facing = ctx.playerFacing;
  }
  state.pose = 'sit';
}

function startHeadJumpTo(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  const target = ctx.headAnchor?.() ?? state.headTarget;
  if (!target) {
    cancelFrogHeadSequence(state, ctx, rng);
    return;
  }
  state.headTarget = target;
  state.phase = 'headJumpTo';
  state.pose = 'hop';
  beginHop(state, target, pickRange(FROG_HEAD_TIMING.headJumpToMs, rng));
}

function onHeadHopLand(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  if (state.phase === 'headJumpTo') {
    state.phase = 'perched';
    state.pose = 'sit';
    state.hop = null;
    state.perchSettleT = 1;
    state.timerMs = pickRange(FROG_HEAD_TIMING.perchMs, rng);
    followHeadPerch(state, ctx);
    return;
  }
  if (state.phase === 'headJumpDown') {
    finishHeadJumpDown(state, ctx, rng);
  }
}

function startHeadJumpDown(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  const target = pickHeadJumpDownTarget(state, ctx, rng);
  if (!target) {
    finishHeadJumpDown(state, ctx, rng);
    return;
  }
  state.phase = 'headJumpDown';
  state.pose = 'hop';
  state.headTarget = target;
  beginHop(state, target, pickRange(FROG_HEAD_TIMING.headJumpDownMs, rng));
}

function finishHeadJumpDown(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  ctx.releaseHeadPerch?.(state.id);
  state.headTarget = null;
  state.perchSettleT = 0;
  state.phase = 'headCooldown';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  state.timerMs = pickRange(FROG_HEAD_TIMING.headCooldownMs, rng);
}

function emergencyLandFrog(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  const near =
    pickHeadJumpDownTarget(state, ctx, rng) ??
    pickRoamTarget(state, ctx, rng, FROG_HOP_DISTANCE);
  if (near) {
    state.x = near.x;
    state.y = near.y;
  }
  state.phase = 'idle';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  const idleSpan = FROG_TIMING.idleMs[1] - FROG_TIMING.idleMs[0];
  state.timerMs =
    FROG_TIMING.idleMs[0] +
    state.idlePhase * idleSpan * 0.5 +
    rng() * idleSpan;
}

function onAmbientHopLand(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  settleIdle(state, ctx, rng);
}

function queueFlee(state: FrogState, rng: () => number): void {
  state.fleeQueued = true;
  state.timerMs = pickRange(FROG_TIMING.reactCrouchMs, rng);
}

function beginReactCrouch(state: FrogState, rng: () => number): void {
  state.phase = 'crouch';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.timerMs = pickRange(FROG_TIMING.reactCrouchMs, rng);
}

function beginAmbientCrouch(state: FrogState, rng: () => number): void {
  state.phase = 'crouch';
  state.pose = 'sit';
  state.fleeQueued = false;
  state.timerMs = pickRange(FROG_TIMING.crouchMs, rng);
}

function startRoamHop(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  const target = pickRoamTarget(state, ctx, rng, FROG_HOP_DISTANCE);
  if (!target) {
    settleIdle(state, ctx, rng);
    return;
  }
  state.phase = 'hopping';
  state.pose = 'hop';
  beginHop(state, target, pickRange(FROG_TIMING.hopMs, rng));
}

function startFleeHop(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  const away = fleeTarget(state, ctx, rng);
  if (!away) {
    settleIdle(state, ctx, rng);
    return;
  }
  state.phase = 'hopping';
  state.pose = 'hop';
  state.fleeQueued = false;
  beginHop(state, away, pickRange(FROG_TIMING.fleeHopMs, rng));
}

function settleIdle(
  state: FrogState,
  _ctx: FrogStepContext,
  rng: () => number,
): void {
  state.phase = 'idle';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  const idleSpan = FROG_TIMING.idleMs[1] - FROG_TIMING.idleMs[0];
  state.timerMs =
    FROG_TIMING.idleMs[0] +
    state.idlePhase * idleSpan * 0.5 +
    rng() * idleSpan;
}

function beginHop(state: FrogState, target: SpawnPoint, durationMs: number): void {
  state.hop = {
    fromX: state.x,
    fromY: state.y,
    toX: target.x,
    toY: target.y,
    elapsedMs: 0,
    durationMs: Math.max(1, durationMs),
  };
  if (Math.abs(target.x - state.x) > 0.001) {
    state.facing = target.x < state.x ? 'left' : 'right';
  }
}

function advanceHop(state: FrogState, dt: number, liftScale: number): void {
  const hop = state.hop;
  if (!hop) return;
  hop.elapsedMs += dt;
  const t = Math.min(1, hop.elapsedMs / hop.durationMs);
  state.x = hop.fromX + (hop.toX - hop.fromX) * t;
  state.y = hop.fromY + (hop.toY - hop.fromY) * t;
  state.liftT = liftScale * 4 * t * (1 - t);
  if (t >= 1) {
    state.x = hop.toX;
    state.y = hop.toY;
    state.liftT = 0;
    state.hop = null;
  }
}

function isLegalSpot(
  point: SpawnPoint,
  state: FrogState,
  ctx: FrogStepContext,
  playerClearance: number,
): boolean {
  if (!pointInPolygon(point.x, point.y, ctx.config.roamPolygon)) return false;
  if (
    frogDistance(point, state.home, ctx.worldAspect) > state.roamRadius
  ) {
    return false;
  }
  if (
    ctx.obstacles &&
    ctx.obstacles.length > 0 &&
    pointInAnyObstacle(point.x, point.y, ctx.obstacles)
  ) {
    return false;
  }
  if (
    ctx.exclusionZones &&
    ctx.exclusionZones.length > 0 &&
    pointInFrogExclusionZone(point, ctx.exclusionZones)
  ) {
    return false;
  }
  if (
    ctx.player &&
    frogDistance(point, ctx.player, ctx.worldAspect) < playerClearance
  ) {
    return false;
  }
  return true;
}

function pickRoamTarget(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
  reach: { min: number; max: number },
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  for (let attempt = 0; attempt < 14; attempt += 1) {
    const angle = rng() * Math.PI * 2;
    const dist = reach.min + rng() * (reach.max - reach.min);
    const candidate = {
      x: state.x + Math.cos(angle) * dist,
      y: state.y + Math.sin(angle) * dist * aspect,
    };
    if (isLegalSpot(candidate, state, ctx, FROG_PLAYER_CLEARANCE)) {
      return candidate;
    }
  }
  return null;
}

function fleeTarget(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const player = ctx.player;
  const baseAngle = player
    ? Math.atan2((state.y - player.y) / aspect, state.x - player.x)
    : rng() * Math.PI * 2;

  const spreads = [0, 0.4, -0.4, 0.85, -0.85];
  for (const spread of spreads) {
    const angle = baseAngle + spread;
    for (const scale of [1, 0.72]) {
      const dist =
        (FROG_FLEE_DISTANCE.min +
          rng() * (FROG_FLEE_DISTANCE.max - FROG_FLEE_DISTANCE.min)) *
        scale;
      const candidate = {
        x: state.x + Math.cos(angle) * dist,
        y: state.y + Math.sin(angle) * dist * aspect,
      };
      if (isLegalSpot(candidate, state, ctx, FROG_PLAYER_CLEARANCE)) {
        return candidate;
      }
    }
  }
  return null;
}

function pickHeadJumpDownTarget(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const origin = ctx.player ?? { x: state.x, y: state.y };
  const toHomeAngle = Math.atan2(
    (state.home.y - origin.y) / aspect,
    state.home.x - origin.x,
  );
  const spreads = [0, 0.35, -0.35, 0.7, -0.7];
  for (const spread of spreads) {
    const angle = toHomeAngle + spread;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const dist = 0.028 + rng() * 0.042;
      const candidate = {
        x: origin.x + Math.cos(angle) * dist,
        y: origin.y + Math.sin(angle) * dist * aspect,
      };
      if (isLegalSpot(candidate, state, ctx, FROG_PLAYER_CLEARANCE * 0.75)) {
        return candidate;
      }
    }
  }
  return pickRoamTarget(state, ctx, rng, {
    min: 0.02,
    max: 0.05,
  });
}

function pickRange(range: readonly [number, number], rng: () => number): number {
  return range[0] + rng() * (range[1] - range[0]);
}

/** Snap or hop away if a frog somehow sits inside a garden bed. */
function ejectFromExclusionIfNeeded(
  state: FrogState,
  ctx: FrogStepContext,
  rng: () => number,
): void {
  if (
    !ctx.exclusionZones?.length ||
    !pointInFrogExclusionZone(state, ctx.exclusionZones) ||
    state.hop
  ) {
    return;
  }
  const escape =
    pickRoamTarget(state, ctx, rng, FROG_HOP_DISTANCE) ?? state.home;
  state.x = escape.x;
  state.y = escape.y;
  if (state.phase === 'idle' || state.phase === 'crouch') {
    state.timerMs = Math.min(state.timerMs, pickRange(FROG_TIMING.crouchMs, rng));
  }
}
